import { Link } from '@inertiajs/react'
import { Layout } from '@/components/Layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Pencil, ExternalLink, RefreshCw } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

interface N8nInstance {
  id: string
  client_id: string
  client_name: string
  name: string
  base_url: string
  timezone: string | null
  sync_status: 'ok' | 'error' | 'never'
  last_workflows_synced_at: string | null
  last_executions_synced_at: string | null
  last_sync_error: string | null
  workflows_count: number
  created_at: string
  updated_at: string
}

interface Props {
  instance: N8nInstance
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleString()
}

function SyncStatusBadge({ status }: { status: N8nInstance['sync_status'] }) {
  switch (status) {
    case 'ok':
      return <Badge variant="success">Synced</Badge>
    case 'error':
      return <Badge variant="destructive">Error</Badge>
    case 'never':
    default:
      return <Badge variant="secondary">Never synced</Badge>
  }
}

export default function N8nInstanceShow({ instance }: Props) {
  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link href="/n8n_instances">
                <ArrowLeft className="h-4 w-4" />
                <span className="sr-only">Back</span>
              </Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{instance.name}</h1>
              <p className="text-muted-foreground">
                <Link href={`/clients/${instance.client_id}`} className="hover:underline">
                  {instance.client_name}
                </Link>
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <a href={instance.base_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Open n8n
              </a>
            </Button>
            <Button asChild>
              <Link href={`/n8n_instances/${instance.id}/edit`}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          </div>
        </div>

        {instance.sync_status === 'error' && instance.last_sync_error && (
          <Alert variant="destructive">
            <AlertTitle>Sync Error</AlertTitle>
            <AlertDescription>{instance.last_sync_error}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Sync Status</CardDescription>
            </CardHeader>
            <CardContent>
              <SyncStatusBadge status={instance.sync_status} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Workflows</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{instance.workflows_count}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Last Workflow Sync</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-sm">{formatDate(instance.last_workflows_synced_at)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Last Execution Sync</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-sm">{formatDate(instance.last_executions_synced_at)}</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Instance Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Base URL</p>
                <a
                  href={instance.base_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 font-medium hover:underline"
                >
                  {instance.base_url}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              {instance.timezone && (
                <div>
                  <p className="text-sm text-muted-foreground">Timezone</p>
                  <p className="font-medium">{instance.timezone}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Sync Actions</CardTitle>
                <CardDescription>
                  Manually trigger a sync for this instance.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Button variant="outline" asChild>
                <Link href={`/sync_health/${instance.id}/sync`} method="post" data={{ type: 'workflows' }}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Sync Workflows
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link href={`/sync_health/${instance.id}/sync`} method="post" data={{ type: 'executions' }}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Sync Executions
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
